import { Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, CheckCircle2, RotateCcw, XCircle, ExternalLink, Download } from 'lucide-react';
import { usePayoutDetail, useUpdatePayoutStatus, useReopenPayout, useDeletePayout } from '@/hooks/useExpertPayouts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function money(n: number, c: string) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n);
}
const statusLabel: Record<string, string> = { draft: 'Borrador', closed: 'Cerrada', paid: 'Pagada', cancelled: 'Cancelada' };

export default function PayoutDetailPage() {
  const { id } = useParams();
  const { data, isLoading } = usePayoutDetail(id);
  const updateStatus = useUpdatePayoutStatus();
  const reopen = useReopenPayout();
  const del = useDeletePayout();

  if (isLoading) return <div className="p-6">Cargando…</div>;
  if (!data) return <div className="p-6">No encontrada. <Link to="/payouts" className="text-primary underline">Volver</Link></div>;

  const { payout, items, expert } = data;
  const currency = payout.currency || 'COP';

  // Agrupar sesiones por cohorte (solo presentación)
  const groupsMap = new Map<string, { key: string; cohortName: string; orgName: string | null; rows: any[]; hours: number; amount: number }>();
  for (const it of items as any[]) {
    const key = it.cohort_id ?? it.cohort_name ?? '__none__';
    let g = groupsMap.get(key);
    if (!g) {
      g = { key, cohortName: it.cohort_name ?? 'Sin cohorte', orgName: it.organization_name ?? null, rows: [], hours: 0, amount: 0 };
      groupsMap.set(key, g);
    }
    g.rows.push(it);
    g.hours += Number(it.duration_hours) || 0;
    g.amount += Number(it.amount) || 0;
  }
  const groups = Array.from(groupsMap.values())
    .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => String(a.session_date).localeCompare(String(b.session_date))) }))
    .sort((a, b) => a.cohortName.localeCompare(b.cohortName));
  const grandHours = groups.reduce((s, g) => s + g.hours, 0);
  const grandAmount = groups.reduce((s, g) => s + g.amount, 0);

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const fmt = (n: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

    doc.setFontSize(16);
    doc.text('Liquidación de Experto', 14, 16);
    doc.setFontSize(10);
    doc.setTextColor(90);
    doc.text(`Experto: ${expert?.full_name ?? '—'}`, 14, 24);
    doc.text(`Email: ${expert?.email ?? '—'}`, 14, 30);
    doc.text(`Período: ${payout.period_month.slice(0, 7)}`, 14, 36);
    doc.text(`Estado: ${statusLabel[payout.status]}`, 120, 24);
    doc.text(`Tarifa: ${fmt(Number(payout.hourly_rate))}/h`, 120, 30);
    doc.text(`Horas: ${Number(payout.total_hours).toFixed(2)}h`, 120, 36);
    doc.setFontSize(12);
    doc.setTextColor(20);
    doc.text(`Total: ${fmt(Number(payout.total_amount))}`, 200, 30);
    if (payout.notes) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text(`Notas: ${payout.notes}`, 14, 44);
    }

    let cursorY = payout.notes ? 52 : 46;
    const head = [['Fecha', 'Título', 'Tipo', 'Duración', 'Grabación', 'Monto']];

    for (const g of groups) {
      if (cursorY > 180) {
        doc.addPage();
        cursorY = 20;
      }
      doc.setFontSize(11);
      doc.setTextColor(20);
      doc.text(`${g.cohortName}${g.orgName ? ` — ${g.orgName}` : ''}`, 14, cursorY);
      doc.setFontSize(9);
      doc.setTextColor(90);
      doc.text(`${g.rows.length} sesiones · ${g.hours.toFixed(2)}h · ${fmt(g.amount)}`, 200, cursorY);

      const rows = g.rows;
      autoTable(doc, {
        head,
        body: rows.map((it: any) => [
          it.session_date,
          it.session_title ?? '',
          it.session_type ?? '',
          `${Number(it.duration_hours).toFixed(2)}h`,
          it.recording_link ? 'Ver grabación' : '—',
          fmt(Number(it.amount)),
        ]),
        startY: cursorY + 3,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [40, 100, 120], textColor: 255 },
        columnStyles: { 4: { textColor: [37, 99, 235] }, 5: { halign: 'right' } },
        didDrawCell: (d) => {
          if (d.section === 'body' && d.column.index === 4) {
            const it: any = rows[d.row.index];
            if (it?.recording_link) {
              doc.link(d.cell.x, d.cell.y, d.cell.width, d.cell.height, { url: it.recording_link });
            }
          }
        },
      });
      cursorY = ((doc as any).lastAutoTable?.finalY ?? cursorY) + 10;
    }

    if (cursorY > 190) {
      doc.addPage();
      cursorY = 20;
    }
    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text(`TOTAL GENERAL: ${grandHours.toFixed(2)}h · ${fmt(grandAmount)}`, 14, cursorY);

    doc.save(`liquidacion-${(expert?.full_name ?? 'experto').replace(/\s+/g, '_')}-${payout.period_month.slice(0, 7)}.pdf`);
  };



  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      <Button asChild variant="ghost" size="sm"><Link to="/payouts"><ArrowLeft className="w-4 h-4" /> Volver</Link></Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-2xl">{expert?.full_name ?? 'Experto'}</CardTitle>
              <p className="text-sm text-muted-foreground">{expert?.email ?? ''} · Período {payout.period_month.slice(0, 7)}</p>
            </div>
            <Badge variant={payout.status === 'paid' ? 'default' : 'secondary'} className="text-sm">{statusLabel[payout.status]}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Horas</div><div className="text-lg font-bold">{Number(payout.total_hours).toFixed(2)}h</div></div>
            <div><div className="text-xs text-muted-foreground">Tarifa</div><div className="text-lg font-bold">{money(Number(payout.hourly_rate), currency)}/h</div></div>
            <div><div className="text-xs text-muted-foreground">Total</div><div className="text-lg font-bold text-primary">{money(Number(payout.total_amount), currency)}</div></div>
            <div><div className="text-xs text-muted-foreground">Sesiones</div><div className="text-lg font-bold">{items.length}</div></div>
          </div>
          {payout.notes && <div className="mt-3 p-3 bg-muted rounded text-sm"><strong>Notas:</strong> {payout.notes}</div>}

          <div className="flex flex-wrap gap-2 mt-4">
            {payout.status === 'draft' && (
              <Button size="sm" onClick={() => updateStatus.mutate({ id: payout.id, status: 'closed' })}>Cerrar</Button>
            )}
            {payout.status === 'closed' && (
              <Button size="sm" onClick={() => updateStatus.mutate({ id: payout.id, status: 'paid' })}><CheckCircle2 className="w-4 h-4" /> Marcar pagada</Button>
            )}
            {payout.status !== 'draft' && (
              <Button size="sm" variant="outline" onClick={() => reopen.mutate(payout.id)}><RotateCcw className="w-4 h-4" /> Reabrir y recalcular</Button>
            )}
            <Button size="sm" variant="outline" onClick={handleDownloadPDF}><Download className="w-4 h-4" /> Descargar PDF</Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { if (confirm('¿Cancelar liquidación?')) del.mutate(payout.id); }}>
              <XCircle className="w-4 h-4" /> Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Sesiones incluidas por cohorte (snapshot)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Grabación</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Sin sesiones.</TableCell></TableRow>}
                {groups.map((g) => (
                  <Fragment key={g.key}>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableCell colSpan={3} className="font-semibold text-sm">
                        {g.cohortName}{g.orgName ? <span className="text-muted-foreground font-normal"> — {g.orgName}</span> : null}
                      </TableCell>
                      <TableCell colSpan={3} className="text-right text-xs text-muted-foreground">
                        {g.rows.length} sesiones · <span className="font-mono">{g.hours.toFixed(2)}h</span> · <span className="font-mono font-semibold text-foreground">{money(g.amount, currency)}</span>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((it: any) => (
                      <TableRow key={it.id}>
                        <TableCell className="text-sm whitespace-nowrap">{it.session_date}</TableCell>
                        <TableCell className="text-sm">{it.session_title}</TableCell>
                        <TableCell><Badge variant="outline">{it.session_type}</Badge></TableCell>
                        <TableCell className="font-mono text-sm">{Number(it.duration_hours).toFixed(2)}h</TableCell>
                        <TableCell>
                          {it.recording_link ? <a href={it.recording_link} target="_blank" rel="noreferrer" className="text-primary text-sm inline-flex items-center gap-1">Ver <ExternalLink className="w-3 h-3" /></a> : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">{money(Number(it.amount), currency)}</TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
                {items.length > 0 && (
                  <TableRow className="border-t-2">
                    <TableCell colSpan={3} className="font-semibold">Total general</TableCell>
                    <TableCell className="font-mono font-semibold">{grandHours.toFixed(2)}h</TableCell>
                    <TableCell />
                    <TableCell className="text-right font-mono font-bold text-primary">{money(grandAmount, currency)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
